import { Breadcrumb, BreadcrumbItem } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import React, { useMemo, useState } from "react";
import { Link, useParams, useNavigate, generatePath } from "react-router-dom";

import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { usePermissions } from "src/authentication/usePermissions.ts";
import { BackButtonWithCondition, Button } from "src/components/Buttons";
import { CaseAlertStatusTag } from "src/components/case/CaseAlertStatusTag";
import { CaseArchivedTag } from "src/components/case/CaseArchivedTag";
import Loading from "src/components/DataStates/Loading.tsx";
import InlineError from "src/components/Errors/InlineError.tsx";
import { ModalTransition } from "src/components/Modal";
import PageHeader from "src/components/PageHeader.tsx";
import PageStack from "src/components/PageStack/PageStack.tsx";
import { SelectedTabType } from "src/components/TabbedView/useTabNavigation.ts";
import { DynamicViewRenderer } from "src/dynamic-view-renderer/DynamicViewRenderer.tsx";
import { useViewDefinition } from "src/dynamic-view-renderer/Views/useViewDefinition";
import {
  CaseVersionsDiffModal,
  hasCaseVersionsChanged,
  OutdatedCaseWarning,
} from "src/features/case/CaseVersionsDiff";
import { useGetCaseByIdQuery } from "src/graphql/anti-fraud/queries/cases/GetCaseByIdQuery.ts";
import { useT } from "src/internationalization";
import { useCaseTypes } from "src/providers/CaseTypesProvider";
import { getOpenAlert } from "src/services/alert";

import { ConfirmCreateAlertModal } from "./ConfirmCreateAlertModal";

const CaseDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const param = useParams<{ id: string; caseTypeCode?: string }>();
  const t = useT();
  const { hasPermission } = usePermissions();

  const { isModalOpen: isConfirmCreateAlertOpen, closeModal, openModal } = useModal();
  const diffModal = useModal();

  const {
    data: caseData,
    error,
    loading,
  } = useGetCaseByIdQuery({
    variables: { id: param.id ?? "" },
    skip: !param.id,
  });

  const hasCaseChanged = useMemo(() => {
    if (!caseData) return null;
    // if there's no source case, then we have nothing to compare against
    if (!caseData.source) return false;

    return hasCaseVersionsChanged(caseData, {
      current: caseData.source,
    });
  }, [caseData]);
  const { caseTypes } = useCaseTypes();

  const viewDefinition = useViewDefinition("CaseDetailsView", caseData?.typeCode);
  const viewConfiguration = viewDefinition?.view;

  const [_selectedTab, setSelectedTab] = useState<SelectedTabType | undefined>();

  const canCreateAlert =
    hasPermission(ScopeResource.ALERT, Scope.CREATE) && caseData?.source === null;
  const openAlert = (caseData ? getOpenAlert(caseData.alerts) : null) && caseData?.source === null;

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <InlineError message={error.message} />;
  }

  const caseName = `${caseData?.configurableType.name}`;
  const caseHeader = `${caseName} ${caseData?.request}`;

  return (
    <>
      <PageStack>
        <Breadcrumb noTrailingSlash={true}>
          <BreadcrumbItem>
            <Link to="/cases">{t("page.case.breadcrumb.cases")}</Link>
          </BreadcrumbItem>
          {caseTypes.length > 1 && (
            <BreadcrumbItem>
              <Link to={`/cases/${param.caseTypeCode}`}>{caseName}</Link>
            </BreadcrumbItem>
          )}
          <BreadcrumbItem isCurrentPage={true}>{caseData?.request}</BreadcrumbItem>
        </Breadcrumb>
        {viewConfiguration && (
          <div className="flex flex-col">
            <div className="flex items-center">
              <BackButtonWithCondition />
              <div className="flex-1 flex items-center justify-between">
                <PageHeader>{caseHeader}</PageHeader>
                {canCreateAlert && !openAlert && (
                  <Button kind="primary" onClick={openModal}>
                    {t("case.actions.createAlert")}
                  </Button>
                )}
                {openAlert && (
                  <Button
                    kind="primary"
                    onClick={() => {
                      if (!param.id) return;
                      navigate(`/alert/${caseData?.alerts[0].id}`);
                    }}
                  >
                    {t("case.actions.openAlert")}
                  </Button>
                )}
                {caseData?.source && caseData.snapshotCreation && (
                  <CaseArchivedTag date={caseData.snapshotCreation} />
                )}
              </div>
            </div>
            <div className="flex gap-3">
              {caseData?.alertStatus && <CaseAlertStatusTag status={caseData.alertStatus} />}
            </div>
          </div>
        )}

        {viewConfiguration && hasCaseChanged && (
          <div className="py-5">
            <OutdatedCaseWarning onClick={diffModal.openModal} />
          </div>
        )}

        {viewConfiguration ? (
          <DynamicViewRenderer
            viewConfiguration={viewConfiguration}
            data={{
              widgetsData: {
                basic: {
                  case: {
                    propertyValues: caseData?.properties,
                    elements: caseData?.elements,
                    blacklistedIdentifiers: caseData?.blacklistedIdentifiers,
                    source: caseData?.source,
                    code: caseData?.typeCode,
                    customProperties: caseData?.source
                      ? [
                          {
                            label: t("component.widget.case.request"),
                            code: "source",
                            type: "Identifier",
                            value: (
                              <Link
                                to={generatePath("/case/:caseTypeCode/:id", {
                                  caseTypeCode: caseData.source.typeCode,
                                  id: caseData.source.id,
                                })}
                              >
                                {caseData.source.request}
                              </Link>
                            ),
                          },
                        ]
                      : [],
                  },
                  propertyValueRendererParams: {
                    CaseLink: {
                      id: caseData?.source?.id || "",
                      caseTypeCode: caseData?.typeCode || "",
                    },
                    Identifier: {
                      snapshotCreation: caseData?.snapshotCreation || null,
                    },
                    IdentifierGraph: {
                      snapshotCreation: caseData?.snapshotCreation || null,
                    },
                  },
                },
                executions: {
                  executionResults: caseData?.executionResults,
                  selectedCase: caseData
                    ? {
                        id: caseData.id,
                        request: caseData.request,
                        alerts: caseData.alerts,
                        typeCode: caseData.typeCode,
                      }
                    : undefined,
                  snapshotMode: caseData?.source !== null,
                },
                caseAlerts: {
                  alerts: caseData?.alerts,
                  snapshotMode: caseData?.source !== null,
                },
              },
            }}
            pathPrefix="/case/:caseTypeCode/:id"
            onNavigate={setSelectedTab}
          />
        ) : (
          <InlineError message={t("general.view.caseType.missing.error")} />
        )}
      </PageStack>
      <ModalTransition>
        {isConfirmCreateAlertOpen && caseData && (
          <ConfirmCreateAlertModal case={caseData} onClose={closeModal} />
        )}
        {caseData?.source && diffModal.isModalOpen && (
          <CaseVersionsDiffModal
            currentCase={caseData.source}
            creationCase={caseData}
            onClose={diffModal.closeModal}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default CaseDetailsPage;
